/**
 * Editor module: editor state (selection, settings, viewport camera), level
 * orchestration, and the bridge from the world to the render backend.
 *
 * Owns the commands that change editor state (`selection.set`,
 * `editor.setSetting`, `camera.*`, `level.open`) and registers them with their
 * writers closed over (review F-1). `level.open` lives here because loading a
 * level coordinates world, assets, selection and undo history (review F-2).
 *
 * Per frame, the "bridge" phase pushes world changes and the camera to the
 * backend. The Renderer module's "render" phase then draws.
 */
import { AssetsModule } from "../assets/AssetsModule";
import type { CommandBus } from "../commands/CommandBus";
import { CommandsModule } from "../commands/CommandsModule";
import { CoreModule } from "../core/CoreModule";
import type { FrameLoop } from "../core/FrameLoop";
import { defineModule } from "../core/module";
import { VersionedStore } from "../core/store";
import type { RenderBackend } from "../render/RenderBackend";
import { RendererModule } from "../render/RendererModule";
import type { EntityId } from "../world/entity";
import type { TransformSystem } from "../world/TransformSystem";
import type { WorldReader } from "../world/World";
import { WorldModule } from "../world/WorldModule";
import { GameModeRegistry } from "../game/GameModeRegistry";
import { registerGameModes } from "../game/modes";
import { PlaySession, playCommands } from "./play/PlaySession";
import { levelCommands, LevelService } from "./levels/LevelService";
import { RenderBridge } from "./render-bridge/RenderBridge";
import { Selection, selectionCommands } from "./selection/Selection";
import { PanelFocus, panelFocusCommands } from "./shell/PanelFocus";
import { levelSaveCommands } from "./levels/levelSaveCommands";
import { Autosave } from "./levels/Autosave";
import { LevelSaves } from "./levels/LevelSaves";
import type { SaveStorage } from "./levels/LevelSaves";
import { EditorSettings, settingsCommands } from "./settings/EditorSettings";
import { driveTour, TourService, tourCommands } from "./tour/TourService";
import { driveAmbientEffects } from "./viewport/ambientEffects";
import { GizmoController } from "./viewport/GizmoController";
import { cameraCommands, startupCameraOf, ViewportCamera } from "./viewport/ViewportCamera";
import { systemTimers } from "../core/Timers";
import type { Timers } from "../core/Timers";
import { EntityClipboard } from "./clipboard/EntityClipboard";
import { PlacementPreview } from "./viewport/PlacementPreview";
import { CameraPreviewController } from "./viewport/CameraPreviewController";
import { CameraPilot, pilotCommands } from "./viewport/CameraPilot";
import type { SystemClipboard } from "./clipboard/EntityClipboard";

export interface EditorOptions {
  /** Previously persisted settings (restored by the host from storage). */
  readonly restoredSettings: Readonly<Record<string, unknown>>;
  /** `prefers-reduced-motion`: ambient viewport effects stay still. */
  readonly reducedMotion: boolean;
  /** Where saved levels are kept (localStorage), or null when there's none. */
  readonly saveStorage: SaveStorage | null;
  /** Delayed work (autosave's wait). */
  readonly timers: Timers;
  /** The system clipboard for copied entities (`navigator.clipboard`), or null when there's none. */
  readonly clipboard: SystemClipboard | null;
}

export const EditorModule = defineModule({
  name: "Editor",
  dependsOn: [CoreModule, CommandsModule, AssetsModule, WorldModule, RendererModule],
  options: (): EditorOptions => ({
    restoredSettings: {},
    reducedMotion: false,
    saveStorage: null,
    timers: systemTimers,
    clipboard: null,
  }),
  init: ({ deps: { Core, Commands, Assets, World, Renderer }, options }) => {
    const selection = new Selection(Core.scheduler);
    const settings = new EditorSettings(Core.scheduler, options.restoredSettings);
    const camera = new ViewportCamera(Core.scheduler);
    const gameModes = new GameModeRegistry();
    registerGameModes(gameModes);
    const panelFocus = new PanelFocus(Core.scheduler);
    const tour = new TourService(Core.scheduler);

    // Saves outlive a reload only if the visitor chose that; otherwise a
    // reload starts from the authored levels (the preference is read once, at boot).
    const saves = new LevelSaves(options.saveStorage, Core.scheduler);
    if (!settings.getSnapshot().keepSavedEdits) saves.clearAll();

    const pilot = new CameraPilot(
      {
        camera,
        world: World.world,
        transforms: World.transforms,
        bus: Commands.bus,
        timers: options.timers,
      },
      Core.scheduler,
    );
    // Fed from the level service's progress below; the bridge is built first, since loads flush it.
    const levelLoading = new VersionedStore<boolean>(Core.scheduler, false);
    const bridge = new RenderBridge({
      world: World.world,
      transforms: World.transforms,
      backend: Renderer.backend,
      selection,
      settings,
      piloting: pilot,
      levelLoading,
      requestFrame: (reason) => {
        Core.frameLoop.invalidate(reason);
      },
    });

    const gizmo = new GizmoController({
      backend: Renderer.backend,
      bus: Commands.bus,
      world: World.world,
      transforms: World.transforms,
      selection,
      settings,
      boundsOf: (ids) => bridge.boundsOf(ids),
      piloting: pilot,
    });

    const levels = new LevelService({
      assets: Assets,
      levels: World.levels,
      world: World.world,
      selection,
      bus: Commands.bus,
      gameModes,
      scheduler: Core.scheduler,
      log: Core.log,
      // The committed level's proxies go to the renderer now (not on the next
      // frame), so the scene it readies is the level's.
      prepareScene: () => {
        bridge.flush();
        return Renderer.backend.prepareScene();
      },
      saves,
    });
    levels.progress.subscribe(() => {
      levelLoading.publish(levels.progress.getSnapshot().phase === "loading");
    });

    registerCommands({ Commands, World, selection, settings, camera, bridge, levels, pilot });
    Commands.bus.register(panelFocusCommands(panelFocus));
    Commands.bus.register(
      levelSaveCommands({
        world: World.world,
        components: World.components,
        index: Assets.index,
        saves,
        levels: World.levels,
        selection,
        authored: () => levels.authoredManifest(),
      }),
    );
    // Only with somewhere to save to (no storage in tests or privacy modes).
    const autosave = options.saveStorage
      ? new Autosave({
          world: World.world,
          timers: options.timers,
          save: () => {
            Commands.bus.execute({ type: "level.save", payload: {}, origin: "autosave" });
          },
        })
      : null;
    Commands.bus.register(tourCommands(tour, selection));
    const stopTour = driveTour({ tour, bus: Commands.bus, world: World.world });
    followStartupCamera({ World, camera });
    const cameraPreview = new CameraPreviewController(
      {
        backend: Renderer.backend,
        world: World.world,
        transforms: World.transforms,
        selection,
        settings,
        camera,
        piloting: pilot,
      },
      Core.scheduler,
    );
    const play = new PlaySession(
      {
        world: World.world,
        transforms: World.transforms,
        camera,
        selection,
        settings,
        gameModes,
        bus: Commands.bus,
        log: Core.log,
        pilot,
      },
      Core.scheduler,
    );
    const playing = playCommands(play);
    Commands.bus.register(playing.sync);
    Commands.bus.registerAsync(playing.start);

    driveFrames({ Core, Renderer, bridge, gizmo, cameraPreview, camera, play });
    const stopEffects = driveAmbientEffects({
      world: World.world,
      settings,
      play,
      frameLoop: Core.frameLoop,
      reducedMotion: options.reducedMotion,
    });

    return {
      selection,
      settings,
      levels,
      gameModes,
      camera,
      bridge,
      gizmo,
      panelFocus,
      tour,
      saves,
      autosave,
      clipboard: new EntityClipboard(options.clipboard),
      placement: new PlacementPreview(Renderer.backend),
      cameraPreview,
      pilot,
      play,
      stopEffects,
      stopTour,
      reducedMotion: options.reducedMotion,
    };
  },
  shutdown: ({ bridge, gizmo, pilot, play, autosave, stopEffects, stopTour }) => {
    autosave?.dispose();
    play.dispose();
    pilot.dispose();
    stopTour();
    stopEffects();
    gizmo.dispose();
    bridge.dispose();
  },
});

// ─── Init helpers ────────────────────────────────────────────────────────

interface CommandWiring {
  readonly Commands: { readonly bus: CommandBus };
  readonly World: {
    readonly world: WorldReader;
    readonly transforms: TransformSystem;
  };
  readonly selection: Selection;
  readonly settings: EditorSettings;
  readonly camera: ViewportCamera;
  readonly bridge: RenderBridge;
  readonly levels: LevelService;
  readonly pilot: CameraPilot;
}

function registerCommands({
  Commands,
  World,
  selection,
  settings,
  camera,
  bridge,
  levels,
  pilot,
}: CommandWiring): void {
  Commands.bus.register([
    ...selectionCommands(selection, World.world),
    ...settingsCommands(settings),
    ...cameraCommands({
      camera,
      world: World.world,
      transforms: World.transforms,
      selection,
      boundsOf: (ids: readonly EntityId[]) => bridge.boundsOf(ids),
      pilot,
    }),
    ...pilotCommands(pilot, World.world),
  ]);
  levelCommands(levels).forEach((definition) => {
    Commands.bus.registerAsync(definition);
  });
}

/** Every level commit starts the view at the level's WorldSettings.startupCamera. */
function followStartupCamera({ World, camera }: Pick<CommandWiring, "World" | "camera">): void {
  World.world.events.on("world:replaced", ({ next }) => {
    const startup = startupCameraOf(next);
    if (startup) camera.viewFromCamera(startup, World.world, World.transforms);
  });
}

interface FrameWiring {
  readonly Core: { readonly frameLoop: FrameLoop };
  readonly Renderer: { readonly backend: RenderBackend };
  readonly bridge: RenderBridge;
  readonly gizmo: GizmoController;
  readonly cameraPreview: CameraPreviewController;
  readonly camera: ViewportCamera;
  readonly play: PlaySession;
}

/**
 * The "bridge" phase: sync proxies, the gizmo and the camera preview (after
 * the bridge, so a moved camera previews from where it is now), then the
 * viewport camera. Camera moves request frames.
 */
function driveFrames({
  Core,
  Renderer,
  bridge,
  gizmo,
  cameraPreview,
  camera,
  play,
}: FrameWiring): void {
  Core.frameLoop.register("bridge", (frame) => {
    // Play moves the view each frame, so a running game keeps the loop going.
    play.tick(frame.dt);
    const running = play.getSnapshot();
    if (running && !running.paused) Core.frameLoop.invalidate("play");

    bridge.flush();
    gizmo.flush();
    cameraPreview.flush();
    Renderer.backend.setCamera(camera.describe());
  });
  camera.subscribe(() => {
    Core.frameLoop.invalidate("camera");
  });
}
